import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { helpers, requiredIf } from '@vuelidate/validators'
import { reactive } from 'vue'
import { ToastInterface, useToast } from 'vue-toastification'

import type { User } from '@/modules/users/interfaces/user'

export interface createUserInterface {
	name: string
	secondName: string
	lastname: string
	secondLastname: string
	birthDate: string
	gender: string
	companyId?: string | number
	roleId: string | number | null
	departmentId?: number
	companyGroupId?: number
	haveAccess: boolean
	email: string | null
	jobTitle: string
	supervisorId?: number | null
}

const getCompanyUser = async (companyId: number, userId: number) => {
	const { data } = await axios.get<User>(
		`/api/companies/${companyId}/users/${userId}`
	)
	return data
}

export const createCompanyUser = async (
	params: createUserInterface,
	toast: ToastInterface
) => {
	try {
		const { data } = await axios.post<User>(
			`/api/companies/${params.companyId}/users`,
			{
				name: params.name,
				secondName: params.secondName,
				lastname: params.lastname,
				secondLastname: params.secondLastname,
				birthDate: params.birthDate,
				gender: params.gender,
				roleId: params.roleId,
				email: params.email,
				companyGroupId:
					params.companyGroupId ? params.companyGroupId : undefined,
				jobTitle: params.jobTitle,
				haveAccess: params.haveAccess,
				departmentId:
					params.departmentId !== 0 ? params.departmentId : undefined,
				supervisorId: params.supervisorId ? params.supervisorId : undefined,
			}
		)

		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const useCompanyUser = (companyId?: number, userId?: number) => {
	const toast = useToast()

	const userForm = reactive<createUserInterface>({
		name: '',
		secondName: '',
		lastname: '',
		secondLastname: '',
		birthDate: '',
		gender: '',
		companyId: '',
		roleId: null,
		email: null,
		jobTitle: '',
		companyGroupId: undefined,
		departmentId: undefined,
		supervisorId: undefined,
		haveAccess: false,
	})

	const createMutation = useMutation((params: createUserInterface) =>
		createCompanyUser(params, toast)
	)

	const restUserForm = () => {
		userForm.name = ''
		userForm.secondName = ''
		userForm.lastname = ''
		userForm.secondLastname = ''
		userForm.birthDate = ''
		userForm.birthDate = ''
		userForm.companyGroupId = undefined
		userForm.departmentId = undefined
		userForm.haveAccess = false
		userForm.email = null
		userForm.jobTitle = ''
		userForm.roleId = null
		userForm.companyId = ''
		userForm.supervisorId = undefined
	}

	const { data, isLoading, isError } = useQuery(
		['companyUser', companyId, userId],
		() => getCompanyUser(companyId!, userId!),
		{
			enabled: !!companyId && !!userId,
		}
	)

	return {
		data,
		isError,
		isLoading,
		createMutation,
		userForm,
		restUserForm,
		createUserMutationAsync: createMutation.mutateAsync,
		createUserMutationLoading: createMutation.isLoading,
		createUserMutationError: createMutation.error,
		createUserMutationIsError: createMutation.isError,
	}
}

export default useCompanyUser
